import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Reveal, MaskLines, MaskLinesInView } from "../motion";
import { CTABand, Marquee } from "../components/ui-kit";
import { AREA_GROUPS, AREAS } from "../content";
import { IMG, LOGO } from "../assets";

const ease = [0.22, 1, 0.36, 1];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-ink">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMG.hero} alt="" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/70" />
      </motion.div>
      <motion.div style={{ opacity }} className="relative h-full mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-col justify-center">
        <h1 className="display text-[3rem] leading-[0.98] sm:text-6xl lg:text-[6.4rem] text-bone max-w-6xl">
          <MaskLines delay={0.35} lines={["Direito conectado à", "operação, aos riscos e às", "decisões do negócio."]} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1, ease }}
          className="mt-10 max-w-2xl text-bone/70 text-lg lg:text-xl font-light leading-relaxed">
          Assessoria empresarial e tributária que compreende a realidade em que cada decisão produz efeitos.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 1, ease }}
          className="mt-10 flex flex-wrap gap-4">
          <Link to="/atuacao" data-testid="hero-cta-primary" className="btn-gold text-base">Conheça nossa atuação <ArrowRight size={18} /></Link>
          <Link to="/contato" data-testid="hero-cta-secondary" className="btn-ghost text-base">Fale com o escritório</Link>
        </motion.div>
      </motion.div>
      <motion.img
        src={LOGO.monogramWhite}
        alt="Monograma Pahl & Burgueño Advogados"
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 0.3, scale: 1 }}
        transition={{ delay: 0.9, duration: 1.8, ease }}
        className="hidden lg:block absolute right-[6%] xl:right-[8%] top-[28%] -translate-y-1/2 w-[320px] xl:w-[380px] object-contain pointer-events-none select-none"
      />
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-bone/40">
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2 }}><ArrowDown size={20} /></motion.div>
      </motion.div>
    </section>
  );
}

function Intro() {
  return (
    <section className="bg-bone py-24 lg:py-36">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-24">
        <Reveal>
          <div className="eyebrow text-gold mb-6">O método</div>
          <h2 className="display text-4xl lg:text-6xl text-ink leading-[1.02]">
            <MaskLinesInView lines={["Uma decisão jurídica", "pode alterar uma", "operação inteira."]} />
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="flex flex-col justify-center gap-6 text-ink/70 text-lg leading-relaxed font-light">
          <p>Tributos repercutem na formação de preços, nos contratos e no fluxo de caixa. Relações de consumo podem revelar falhas recorrentes em processos e atendimento. Uma disputa exige avaliar a prova disponível, os custos e a continuidade do negócio.</p>
          <p>Questões jurídicas empresariais raramente permanecem restritas a uma única área. O escritório procura compreender como o problema se formou e quais efeitos cada alternativa pode produzir — antes de definir o caminho jurídico.</p>
          <Link to="/escritorio" data-testid="intro-link" className="text-ink font-medium link-underline inline-flex items-center gap-2 w-fit">Conheça o escritório <ArrowRight size={16} className="text-gold" /></Link>
        </Reveal>
      </div>
    </section>
  );
}

function Frentes() {
  return (
    <section className="bg-ink-deep text-bone py-24 lg:py-32 grain overflow-hidden">
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <Reveal className="mb-16 max-w-3xl">
          <div className="eyebrow text-gold mb-6">Atuação</div>
          <h2 className="display text-4xl lg:text-6xl">Quatro frentes. Uma leitura integrada de cada problema.</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
          {AREA_GROUPS.map((g, i) => (
            <Reveal key={g.group} delay={i * 0.08} className="bg-ink-deep p-8 lg:p-12 hover:bg-ink-soft transition-colors group">
              <div className="flex items-start justify-between mb-6">
                <span className="serif text-gold/40 text-4xl">0{i + 1}</span>
              </div>
              <h3 className="serif text-2xl lg:text-3xl mb-5 text-bone group-hover:text-gold transition-colors">{g.group}</h3>
              <ul className="space-y-2.5">
                {g.items.map((it) => (
                  <li key={it.slug}>
                    <Link to={`/atuacao/${it.slug}`} data-testid={`frente-${it.slug}`} className="text-bone/60 hover:text-gold transition-colors text-[0.95rem] flex items-center gap-2 link-underline w-fit">
                      <span className="text-gold">—</span> {it.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1} className="mt-12">
          <Link to="/atuacao" data-testid="frentes-cta" className="btn-gold">Ver toda a atuação <ArrowRight size={18} /></Link>
        </Reveal>
      </div>
    </section>
  );
}

function Reforma() {
  return (
    <section className="bg-bone py-24 lg:py-36 overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <Reveal className="relative">
          <div className="relative rounded-2xl overflow-hidden aspect-[4/5] max-w-md">
            <img src={IMG.office} alt="Detalhe arquitetônico do escritório" className="w-full h-full object-cover" />
            <div className="absolute inset-0 ring-1 ring-inset ring-ink/10 rounded-2xl" />
          </div>
          <div className="absolute -bottom-6 -right-2 lg:right-8 bg-ink text-bone rounded-xl px-6 py-5 shadow-2xl">
            <div className="serif text-gold text-3xl">IBS · CBS · IS</div>
            <div className="text-bone/50 text-xs mt-1">Reforma Tributária do Consumo</div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="eyebrow text-gold mb-6">Reforma tributária</div>
          <h2 className="display text-4xl lg:text-6xl text-ink leading-[1.02] mb-7">Da norma à implementação.</h2>
          <p className="text-ink/70 text-lg leading-relaxed font-light mb-5">A Reforma Tributária do Consumo altera mais do que a apuração de tributos. Seus efeitos alcançam contratos, preços, créditos, documentos fiscais, cadastros, sistemas, fornecedores, clientes e processos internos.</p>
          <p className="text-ink/70 text-lg leading-relaxed font-light mb-8">O escritório apoia empresas na preparação da transição e na definição das adaptações relacionadas ao IBS, à CBS e ao Imposto Seletivo, em diálogo com as áreas responsáveis pela execução.</p>
          <Link to="/atuacao/direito-tributario" data-testid="reforma-cta" className="text-ink font-medium link-underline inline-flex items-center gap-2">Conheça a atuação tributária <ArrowRight size={16} className="text-gold" /></Link>
        </Reveal>
      </div>
    </section>
  );
}

const STEPS = [
  { n: "01", h: "Compreensão do contexto", p: "Antes de recomendar uma medida, o escritório examina os fatos, a dinâmica entre os envolvidos, a documentação disponível, a urgência e os possíveis efeitos da decisão." },
  { n: "02", h: "Definição da estratégia", p: "As alternativas são avaliadas sob perspectivas jurídicas e práticas — prevenção, negociação, revisão de procedimentos, atuação administrativa ou processo judicial." },
  { n: "03", h: "Acompanhamento direto", p: "Lucas e Rodrigo participam da análise, da definição do caminho e da comunicação com o cliente, preservando o contexto e a continuidade do trabalho." },
];

function Metodo() {
  return (
    <section className="bg-ink text-bone py-24 lg:py-32 grain overflow-hidden">
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <Reveal className="max-w-3xl mb-16">
          <div className="eyebrow text-gold mb-6">Atuação direta dos sócios</div>
<h2 className="display text-4xl lg:text-6xl">
  <span className="block lg:whitespace-nowrap">
    A participação direta integra o
  </span>
  <span className="block mt-4">
    método de trabalho.
  </span>
</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-10 lg:gap-14">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1} className="border-t border-white/15 pt-7">
              <div className="serif text-gold text-5xl mb-5">{s.n}</div>
              <h3 className="serif text-2xl mb-4 text-bone">{s.h}</h3>
              <p className="text-bone/55 leading-relaxed font-light text-[0.98rem]">{s.p}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Intro />
      <Frentes />
      <Reforma />
      <Marquee />
      <Metodo />
      <CTABand
        title={["Apresente o contexto.", "O escritório cuida", "do direcionamento."]}
        text="Uma descrição breve do assunto, das pessoas ou empresas envolvidas e de eventual prazo permite verificar a disponibilidade, possíveis conflitos de interesses e o canal adequado para continuidade."
      />
    </>
  );
}
